import GenderSelect from './gender-select';
import UiTracking from '../tracking/ui-tracking';
import { DynamicConf, GenderSelection } from '@parship/psg-interfaces';

export default class SocialSignOn {
	private readonly form: HTMLFormElement;
	private inputContainer: HTMLElement;
	private appleRegButton: HTMLAnchorElement;
	private googleRegButton: HTMLAnchorElement;
	private dynamicConf: DynamicConf;
	private genderSelect: GenderSelect;
	private uiTrackingEnabled: boolean;

	public constructor(
		container: HTMLFormElement,
		genderSelect: GenderSelect,
		dynamicConf: DynamicConf
	) {
		this.form = container;
		this.genderSelect = genderSelect;
		this.dynamicConf = dynamicConf;
		this.uiTrackingEnabled = this.form.dataset.biEnabled === 'true';
		this.inputContainer = this.form.querySelector('.social-sign-on');

		if (
			this.dynamicConf?.apple.isRegistrationAllowed &&
			this.form.querySelector('.apple-reg-container') !== null
		) {
			this.appleRegButton = this.form.querySelector('.apple-reg-button a');
			this.appleRegButton?.addEventListener('click', (event: Event) =>
				this.onSocialLinkClicked(event)
			);

			this.form.querySelector('.social-sign-on-separator')?.classList.add('is-visible');
			this.form.querySelector('.apple-reg-container')?.classList.add('is-visible');
		}

		if (
			this.dynamicConf?.google?.isRegistrationAllowed &&
			this.form.querySelector('.google-reg-container') !== null
		) {
			this.googleRegButton = this.form.querySelector('.google-reg-button');
			this.googleRegButton.addEventListener('click', (event: Event) => {
				this.onSocialLinkClicked(event);
			});
			this.form.querySelector('.social-sign-on-separator')?.classList.add('is-visible');
			this.form.querySelector('.google-reg-container')?.classList.add('is-visible');
			this.setGoogleButtonHref(null);
		}

		this.initUiTracking();
	}

	private onSocialLinkClicked(event: Event): void {
		if (this.genderSelect && !this.genderSelect.validate()) {
			event.preventDefault();
		}
	}

	public setAppleButtonHref(selectedSex: GenderSelection): void {
		if (!this.appleRegButton) {
			return;
		}

		if (!this.dynamicConf.apple.isRegistrationAllowed) {
			return;
		}

		const redirectUrl: string = encodeURIComponent(
			`${this.dynamicConf.apple.callbackDomain}/appleCallback`
		);

		let stateParams: string = `action=reg&oid=${this.dynamicConf.ownerId}&search=${
			selectedSex.mySex + selectedSex.esSex
		}`;

		if (this.dynamicConf.apple.socialProviderState?.length) {
			stateParams += `&socialProviderState=${this.dynamicConf.apple.socialProviderState}`;
		}

		const state: string = encodeURIComponent(stateParams);
		const responseType: string = encodeURIComponent('code id_token');
		const responseMode: string = 'form_post';
		const scope: string = encodeURIComponent('email name');

		this.appleRegButton.href = `https://appleid.apple.com/auth/authorize?client_id=${this.dynamicConf.apple.clientId}&redirect_uri=${redirectUrl}&response_type=${responseType}&response_mode=${responseMode}&scope=${scope}&state=${state}`;
	}

	public setGoogleButtonHref(selectedSex: GenderSelection): void {
		if (!this.googleRegButton) {
			return;
		}

		const href: URL = new URL('https://accounts.google.com/o/oauth2/v2/auth');
		href.searchParams.append('client_id', this.dynamicConf.google.clientId);
		href.searchParams.append('response_type', 'code');
		href.searchParams.append('access_type', 'offline');
		href.searchParams.append('scope', 'profile email');
		href.searchParams.append(
			'redirect_uri',
			`${this.dynamicConf.ownerUrl}/profile/registration`
		);

		let state: string = '';

		if (selectedSex) {
			state = `socialProvider=GOOGLE&gender=${selectedSex.gender}&mysex=${selectedSex.mySex}&searchGenders=${selectedSex.searchGenders}`;
		}

		if (this.dynamicConf.google.socialProviderState?.length) {
			state += `${state ? '&' : ''}socialProviderState=${this.dynamicConf.google.socialProviderState}`;
		}

		if (state) {
			href.searchParams.append('state', encodeURIComponent(state));
		}

		this.googleRegButton.href = href.toString();
	}

	private initUiTracking(): void {
		if (!this.uiTrackingEnabled) {
			return;
		}

		this.appleRegButton?.addEventListener(
			'click',
			(event: Event) => {
				UiTracking.trackUIEvent('registration-form', 'apple-reg-button', event.type);
			},
			{ once: true }
		);

		this.googleRegButton?.addEventListener(
			'click',
			(event: Event) => {
				UiTracking.trackUIEvent('registration-form', 'google-reg-button', event.type);
			},
			{ once: true }
		);
	}

	public enableUi(): void {
		if (this.appleRegButton || this.googleRegButton) {
			this.inputContainer.inert = false;
		}
	}
}
